import some from 'lodash/some';
import has from 'lodash/has';
import filter from 'lodash/filter';
import forEach from 'lodash/forEach';
import mapValues from 'lodash/mapValues';
import isPlainObject from 'lodash/isPlainObject';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';

import isArray from 'lodash/isArray';
import jsonpath from 'jsonpath';
import xpath from 'xpath';
import { selectXPath } from './XMLUtils';
import UIUtils from './UIUtils';

const emailPattern =
  /^[-!#$%&'*+/0-9=?A-Z^_a-z{|}~](\.?[-!#$%&'*+/0-9=?A-Z^_a-z`{|}~])*@[a-zA-Z0-9](-?\.?[a-zA-Z0-9])*\.[a-zA-Z](-?[a-zA-Z0-9])+$/;
const passwordPattern = /^(?=.*[a-zA-Z])(?=.*[0-9])(?=.*[\W]).{8,}$/;
// Database column name patterns
const redshiftColumnNamePattern = /^[a-z_][a-z0-9_$]{0,126}$/;
const redshiftSuffixPattern = /^[a-z0-9_$]{0,127}$/;

const postgresColumnNamePattern = /^[a-zA-Z_][\w$]{0,62}$/;
const postgresSuffixPattern = /^[\w$]{0,63}$/;

const gcpAlloyDBColumnNamePattern = /^[a-zA-Z_][\w$]{0,62}$/;
const gcpAlloyDBSuffixPattern = /^[\w$]{0,63}$/;

const cloudsqlPostgresColumnNamePattern = /^[a-zA-Z_][\w$]{0,62}$/;
const cloudsqlPostgresSuffixPattern = /^[\w$]{0,63}$/;

const supabaseColumnNamePattern = /^[a-zA-Z_][\w$]{0,62}$/;
const supabaseSuffixPattern = /^[\w$]{0,63}$/;

const snowflakeColumnNamePattern = /^[A-Z_][A-Z0-9_]{0,254}$/;
const snowflakeSuffixPattern = /^[A-Z0-9_]{0,255}$/;

const oracleAutonomousColumnNamePattern = /^[A-Z_][A-Z0-9_]{0,255}$/;
const oracleAutonomousSuffixPattern = /^[A-Z0-9_]{0,256}$/;

const mysqlColumnNamePattern = /^\w[\w$]{0,63}$/;
const mysqlSuffixPattern = /^[\w$]{0,64}$/;

const cloudsqlMysqlColumnNamePattern = /^\w[\w$]{0,63}$/;
const cloudsqlMysqlSuffixPattern = /^[\w$]{0,64}$/;

const fireboltColumnNamePattern = /^\w[\w$]{0,63}$/;
const fireboltSuffixPattern = /^[\w$]{0,64}$/;

const databricksColumnNamePattern = /^[a-z_][a-z0-9_]{0,126}$/;
const databricksSuffixPattern = /^[a-z0-9_]{0,127}$/;

const netsuiteJdbcColumnNamePattern = /^[a-zA-Z_][\w$]{0,62}$/;
const netsuiteJdbcSuffixPattern = /^[\w$]{0,63}$/;

const gcpSpannerColumnNamePattern = /^[a-zA-Z_][a-zA-Z0-9_]{0,128}$/;
const gcpSpannerSuffixPattern = /^[a-zA-Z0-9_]{0,129}$/;

const sybaseColumnNamePattern = /^[A-Z_][A-Z0-9_]{0,255}$/;
const sybaseSuffixPattern = /^[A-Z0-9_]{0,256}$/;
const invalidColumnNameCharPattern = /[^\w$]/g;

export function isValidEmail(email) {
  if (!email) {
    return false;
  }

  return emailPattern.test(email);
}

export function isValidPassword(password) {
  if (!password) {
    return false;
  }

  return passwordPattern.test(password);
}

export function containsInvalidEmails(emails) {
  return some(emails, (email) => {
    return !emailPattern.test(email);
  });
}

export function isValidDBColumnName(name, connectionType, options = {}) {
  // Input validation
  if (!name || typeof name !== 'string') {
    return false;
  }

  const {
    skipBeginWithCheck = false,
    skipOnlyNumbersCheck = false, // Default to false (check numbers by default)
  } = options;

  // Choose the appropriate pattern based on skipBeginWithCheck
  let pattern;
  let hasNumbersCheck = false; // Only certain databases have "cannot be all numbers" check

  switch (connectionType) {
    case 'redshift':
      pattern = skipBeginWithCheck ? redshiftSuffixPattern : redshiftColumnNamePattern;
      break;

    case 'postgres':
      pattern = skipBeginWithCheck ? postgresSuffixPattern : postgresColumnNamePattern;
      break;

    case 'gcp_alloydb':
      pattern = skipBeginWithCheck ? gcpAlloyDBSuffixPattern : gcpAlloyDBColumnNamePattern;
      break;

    case 'cloudsql_postgres':
      pattern = skipBeginWithCheck
        ? cloudsqlPostgresSuffixPattern
        : cloudsqlPostgresColumnNamePattern;
      break;

    case 'supabase':
      pattern = skipBeginWithCheck ? supabaseSuffixPattern : supabaseColumnNamePattern;
      break;

    case 'snowflake':
    case 'snowflake_dcr':
      pattern = skipBeginWithCheck ? snowflakeSuffixPattern : snowflakeColumnNamePattern;
      break;

    case 'oracle_autonomous':
      pattern = skipBeginWithCheck
        ? oracleAutonomousSuffixPattern
        : oracleAutonomousColumnNamePattern;
      break;

    case 'mysql':
      pattern = skipBeginWithCheck ? mysqlSuffixPattern : mysqlColumnNamePattern;
      hasNumbersCheck = true; // MySQL has "cannot be all numbers" check
      break;

    case 'cloudsql_mysql':
      pattern = skipBeginWithCheck ? cloudsqlMysqlSuffixPattern : cloudsqlMysqlColumnNamePattern;
      hasNumbersCheck = true; // CloudSQL MySQL has "cannot be all numbers" check
      break;

    case 'firebolt':
      pattern = skipBeginWithCheck ? fireboltSuffixPattern : fireboltColumnNamePattern;
      hasNumbersCheck = true; // Firebolt has "cannot be all numbers" check
      break;

    case 'databricks':
      pattern = skipBeginWithCheck ? databricksSuffixPattern : databricksColumnNamePattern;
      hasNumbersCheck = true; // Databricks has "cannot be all numbers" check
      break;

    case 'netsuite_jdbc':
      pattern = skipBeginWithCheck ? netsuiteJdbcSuffixPattern : netsuiteJdbcColumnNamePattern;
      hasNumbersCheck = true; // NetSuite JDBC has "cannot be all numbers" check
      break;

    case 'gcp_spanner':
      pattern = skipBeginWithCheck ? gcpSpannerSuffixPattern : gcpSpannerColumnNamePattern;
      hasNumbersCheck = true; // GCP Spanner has "cannot be all numbers" check
      break;

    case 'sybase':
      pattern = skipBeginWithCheck ? sybaseSuffixPattern : sybaseColumnNamePattern;
      break;

    default:
      return true;
  }

  // Use the pattern for validation
  const isValid = pattern.test(name);

  // Apply "cannot be all numbers" check if:
  // 1. Pattern validation passed
  // 2. We want to check numbers (skipOnlyNumbersCheck === false)
  // 3. Database has this check (hasNumbersCheck === true)
  if (isValid && skipOnlyNumbersCheck === false && hasNumbersCheck === true) {
    return !/^\d+$/.test(name);
  }

  return isValid;
}

export function getTableNameValidationMessage(connectionType, fieldName) {
  const rules = {
    redshift:
      'must start with a letter or underscore and contain only lowercase letters, numbers, underscores, and $ (max 127 characters)',
    postgres:
      'must start with a letter or underscore and contain only letters, numbers, underscores, and $ (max 63 characters)',
    gcp_alloydb:
      'must start with a letter or underscore and contain only letters, numbers, underscores, and $ (max 63 characters)',
    cloudsql_postgres:
      'must start with a letter or underscore and contain only letters, numbers, underscores, and $ (max 63 characters)',
    supabase:
      'must start with a letter or underscore and contain only letters, numbers, underscores, and $ (max 63 characters)',
    snowflake:
      'must start with a letter or underscore and contain only uppercase letters, numbers, and underscores (max 255 characters)',
    oracle_autonomous:
      'must start with a letter or underscore and contain only uppercase letters, numbers, and underscores (max 256 characters)',
    mysql:
      'must start with a letter, number, or underscore and contain only letters, numbers, underscores, and $ (max 64 characters, cannot be all numbers)',
    cloudsql_mysql:
      'must start with a letter, number, or underscore and contain only letters, numbers, underscores, and $ (max 64 characters, cannot be all numbers)',
    firebolt:
      'must start with a letter, number, or underscore and contain only letters, numbers, underscores, and $ (max 64 characters, cannot be all numbers)',
    databricks:
      'must start with a letter or underscore and contain only lowercase letters, numbers, underscores, and $ (max 127 characters)',
    netsuite_jdbc:
      'must start with a letter or underscore and contain only letters, numbers, underscores, and $ (max 63 characters)',
    gcp_spanner:
      'must start with a letter or underscore and contain only letters, numbers, and underscores (max 129 characters)',
    sybase:
      'must start with a letter or underscore and contain only uppercase letters, numbers, and underscores (max 256 characters)',
  };

  const rule =
    rules[connectionType] ||
    'must start with a letter or underscore and contain only letters, numbers, underscores, and $';
  return `${fieldName.charAt(0).toUpperCase() + fieldName.slice(1)} is invalid. Table name ${rule}.`;
}

export function convertToValidDBColumnName(name, connectionType) {
  if (!name) {
    return name;
  }

  let columnName = name.replace(invalidColumnNameCharPattern, '_');

  switch (connectionType) {
    case 'redshift':
    case 'databricks':
      columnName = columnName.toLowerCase();
      break;

    case 'snowflake':
    case 'snowflake_dcr':
    case 'oracle_autonomous':
      columnName = columnName.toUpperCase();
      break;

    default:
  }

  return columnName;
}

/**
 * Validates field validations from connector specs (pattern, notNull, notEmpty, etc.)
 * @param {Array} fields - Array of field definitions from connector spec
 * @param {Object} values - Object containing field values to validate
 * @returns {Array} Array of validation error objects with field info and error message
 */
export function validateFieldValidations(fields, values) {
  const errors = [];

  forEach(fields, (field) => {
    if (!field) {
      return;
    }

    // Check if field should be rendered (respects dependsOn conditions)
    const isVisible = UIUtils.shouldRenderField(field, values);
    if (!isVisible) {
      return;
    }

    const fieldValue = get(values, field.name, '') || get(values, field.fieldName, '');

    // Validate pattern validation
    if (has(field, 'validations.pattern')) {
      const patternValidation = field.validations.pattern;
      const regex = patternValidation.regex;

      // Skip validation if field is empty and not required (optional fields)
      if (!fieldValue && !field.required) {
        return;
      }

      if (regex) {
        try {
          // Remove leading/trailing slashes if present (some specs use /pattern/ format)
          let cleanRegex = regex;
          if (regex.startsWith('/')) {
            const lastSlash = regex.lastIndexOf('/');
            if (lastSlash > 0) {
              cleanRegex = regex.substring(1, lastSlash);
            }
          }

          // Test the value against the regex pattern
          const regexPattern = new RegExp(cleanRegex);
          const isValid = regexPattern.test(fieldValue || '');

          if (!isValid) {
            errors.push({
              field: field.name,
              displayName: field.display_name || field.name,
              message: patternValidation.message || `Field does not match required pattern`,
            });
          }
        } catch (error) {
          // Invalid regex pattern, skip validation
          console.warn(`Invalid regex pattern for field ${field.name}:`, regex);
        }
      }
    }

    // Validate notNull validation
    if (has(field, 'validations.notNull')) {
      const shouldNotBeNull = field.validations.notNull;
      if (shouldNotBeNull && (fieldValue === null || fieldValue === undefined)) {
        errors.push({
          field: field.name,
          displayName: field.display_name || field.name,
          message: 'This field cannot be null',
        });
      }
    }

    // Validate notEmpty validation
    if (has(field, 'validations.notEmpty')) {
      const shouldNotBeEmpty = field.validations.notEmpty;
      if (shouldNotBeEmpty && field.type === 'string' && fieldValue === '') {
        errors.push({
          field: field.name,
          displayName: field.display_name || field.name,
          message: 'This field cannot be empty',
        });
      }
    }
  });

  return errors;
}

export function getEmptyRequiredFields(fields, values) {
  const missingFields = filter(fields, (field) => {
    if (!field) {
      return false;
    }

    const { required } = field;

    if (!required || has(field, 'default') || field.type === 'button') {
      return false;
    }

    // checking if this field is required on the current page or not.
    const isVisible = UIUtils.shouldRenderField(field, values || {});

    if (!isPlainObject(required)) {
      // if it is a boolean
      return (
        isVisible &&
        !(
          (values && values[field.name]) ||
          (values && values[field.fieldName]) ||
          (values && values[field.name] === 0)
        )
      );
    } else {
      if (!isVisible) {
        //if field is not rendered it is not required
        return false;
      } else {
        let isRequired = UIUtils.shouldRenderField(
          {
            ...field,
            dependsOn: required,
          },
          values,
        );

        if (!isRequired) {
          return false;
        } else {
          // if value exist;
          let condition = true;
          let fieldValue = get(values, field.name, '') || get(values, field.fieldName, '');
          if (isPlainObject(fieldValue) && isEmpty(fieldValue)) {
            condition = true;
          } else if (fieldValue || fieldValue === 0) {
            condition = false;
          }

          return condition;
        }
      }
    }
  });

  // Check if any field has pattern validation to minimize blast radius
  // Only run validation engine if pattern validations are present
  const hasPatternValidation = fields.some((field) => field && has(field, 'validations.pattern'));

  if (hasPatternValidation) {
    // Run validation checks only when pattern validations exist
    const validationErrors = validateFieldValidations(fields, values);

    // Use Set/Map for O(1) lookups instead of O(n) array searches
    const missingFieldNames = new Set(missingFields.map((f) => f.name));
    const fieldsByName = new Map();
    fields.forEach((f) => {
      if (f) {
        if (f.name) fieldsByName.set(f.name, f);
        if (f.fieldName) fieldsByName.set(f.fieldName, f);
      }
    });

    // Add validation errors as fields with validationError property for backward compatibility
    validationErrors.forEach((error) => {
      // Only add if field is not already in missingFields (O(1) lookup)
      if (!missingFieldNames.has(error.field)) {
        // Find the field definition using Map (O(1) lookup)
        const fieldDef = fieldsByName.get(error.field);
        if (fieldDef) {
          missingFields.push({
            ...fieldDef,
            validationError: error.message,
          });
        }
      }
    });
  }

  return missingFields;
}

/**
 * Formats validation errors into a user-friendly message string.
 * Consolidates missing required fields and validation errors into a single message.
 * @param {Array} missingFields - Array of field objects from getEmptyRequiredFields()
 * @param {Object} options - Optional configuration
 * @param {string} options.credentialNameError - Optional error message for credential name
 * @returns {string|null} Formatted error message or null if no errors
 */
export function formatValidationErrorMessage(missingFields, options = {}) {
  const { credentialNameError } = options;
  const validationErrors = missingFields.filter((field) => field.validationError);
  const requiredFieldErrors = missingFields.filter((field) => !field.validationError);

  const errors = [];

  if (credentialNameError) {
    errors.push(credentialNameError);
  }

  if (requiredFieldErrors.length) {
    const fieldNames = requiredFieldErrors
      .map((field) => field.display_name || field.name)
      .join(', ');
    errors.push(`Missing required fields: ${fieldNames}`);
  }

  if (validationErrors.length) {
    errors.push(
      ...validationErrors.map(
        (field) => `${field.display_name || field.name}: ${field.validationError}`,
      ),
    );
  }

  if (!errors.length) return null;

  return errors.length === 1 ? errors[0] : `${errors.length} errors: ${errors.join(' | ')}`;
}

export function validateDataPath(dataPathsByFieldName, apiResponse, stageAPIConfig) {
  if (isEmpty(dataPathsByFieldName)) {
    return {
      status: false,
      message: 'No data paths found.',
    };
  }

  let apiResponseContent;
  let apiResponseContentType;
  let apiResponseCode;

  if (apiResponse) {
    apiResponseContent = apiResponse.apiResponseContent;
    apiResponseContentType = apiResponse.apiResponseContentType;
    apiResponseCode = apiResponse.apiResponseCode;
  }

  return mapValues(dataPathsByFieldName, (dataPath) => {
    if (!apiResponseContent || apiResponseCode >= 400) {
      return {
        status: false,
        message: `Failed to load URL ${stageAPIConfig['url.template']}.`,
      };
    }

    switch (apiResponseContentType) {
      case 'application/json': {
        let response;
        try {
          response = JSON.parse(apiResponseContent);
        } catch (error) {
          return {
            status: false,
            message: 'Failed to parse JSON response',
          };
        }

        let matches;
        try {
          matches = jsonpath.query(response, dataPath);
        } catch (error) {
          return {
            status: false,
            message: 'Invalid JSONPath syntax',
            preventCreation: true,
          };
        }

        if (!matches || matches.length === 0) {
          return {
            status: false,
            message: 'No match found',
            matches,
          };
        }

        if (isEmpty(matches) || !isArray(matches)) {
          return {
            status: false,
            message: 'No array or object found',
            matches,
          };
        }

        const idProperty = stageAPIConfig['response.id.field.name'];
        if (stageAPIConfig.isPaginated && idProperty) {
          // check that every item of the matches has response.id.field.name property
          for (let i = matches.length - 1; i > -1; i--) {
            if (!has(matches[i], idProperty)) {
              return {
                status: false,
                message: `Some items missing "${idProperty}"`,
                matches,
              };
            }
          }
        }

        return {
          status: true,
          message: `${matches.length} records found`,
          matches,
        };
      }

      case 'application/xml': {
        let matches;
        try {
          matches = selectXPath(dataPath, apiResponseContent);
        } catch (error) {
          return {
            status: false,
            message: 'Failed to parse XML response',
          };
        }

        if (isEmpty(matches)) {
          return {
            status: false,
            message: 'No match found',
            matches,
          };
        }

        if (stageAPIConfig.isPaginated) {
          // check that every item of the matches has response.id.field.name property
          const idProperty = stageAPIConfig['response.id.field.name'];
          for (let i = matches.length - 1; i > -1; i--) {
            let idNode;
            try {
              idNode = xpath.select(idProperty, matches[i]);
            } catch (error) {}

            if (isEmpty(idNode)) {
              return {
                status: false,
                message: `Some items missing "${idProperty}"`,
                matches,
              };
            }
          }
        }

        return {
          status: true,
          message: `${matches.length} records found`,
          matches,
        };
      }

      default: {
        return {
          status: false,
          message: `Unknown content type: ${apiResponseContentType}`,
        };
      }
    }
  });
}
